import { useCallback } from 'react';
import { useAuth, getIdToken } from '@pooflabs/web';
import { notify } from '@/utils/notifications';
import { getPoofAPIUrl, getPoofAPIHeaders } from '@/lib/config';
import { haptic } from '@/utils/haptic';
import { WallpapersResponse } from '@/lib/collections/wallpapers';

function isLiveWallpaper(wallpaper: WallpapersResponse): boolean {
  const stored = (wallpaper as WallpapersResponse & { mediaType?: string }).mediaType;
  if (stored === 'live-wallpaper') return true;
  if (stored && stored !== 'live-wallpaper') return false;
  // Legacy fallback: detect from URL extension
  const url = (wallpaper.imageUrl || '').toLowerCase().split('?')[0];
  return /\.(mp4|webm|mov|m4v)$/.test(url);
}

function isRingtone(wallpaper: WallpapersResponse): boolean {
  const stored = (wallpaper as WallpapersResponse & { mediaType?: string }).mediaType;
  return stored === 'ringtone';
}

function getFullImageUrl(wallpaper: WallpapersResponse): string {
  const baseUrl = wallpaper.imageUrl;
  const stripped = baseUrl.split('?')[0];
  const isUnsplash = stripped.includes('unsplash.com') || stripped.includes('images.unsplash');
  // Always serve max quality for downloads
  return isUnsplash ? `${stripped}?w=2160&q=90&auto=format` : baseUrl;
}

/**
 * Shared download logic for wallpapers/live-wallpapers/ringtones — the fetch → blob →
 * anchor-click dance, plus the fire-and-forget downloadCount increment.
 *
 * Used by both WallpaperViewer (Download icon, shown when purchased or admin) and
 * WallpaperCard (admin context-menu "Download" entry). Authorization (purchased OR
 * admin) is enforced server-side by /api/download-image — this hook doesn't gate on
 * isPurchased/isAdmin itself, it just triggers the download flow.
 */
export function useWallpaperDownload() {
  const { user } = useAuth();

  const downloadWallpaper = useCallback(async (wallpaper: WallpapersResponse) => {
    haptic.light();
    if (!user) {
      haptic.error();
      notify.error('Please connect your wallet to download');
      return;
    }

    try {
      const token = await getIdToken();
      if (!token) {
        notify.error('Authentication required — please reconnect your wallet', user.address);
        return;
      }

      const imageUrl = getFullImageUrl(wallpaper);

      // Determine the correct extension: derive from URL first, then fall back by media type
      const urlExtMatch = imageUrl.split('?')[0].match(/\.(\w{2,4})$/);
      const urlExt = urlExtMatch ? urlExtMatch[1].toLowerCase() : null;
      let ext: string;
      if (urlExt) {
        ext = urlExt;
      } else if (isRingtone(wallpaper)) {
        ext = 'mp3';
      } else if (isLiveWallpaper(wallpaper)) {
        ext = 'mp4';
      } else {
        ext = 'jpg';
      }
      const downloadFilename = `${wallpaper.title ?? 'file'}.${ext}`;
      const proxyUrl = getPoofAPIUrl(
        `/api/download-image?wallpaperId=${encodeURIComponent(wallpaper.id)}&url=${encodeURIComponent(imageUrl)}&token=${encodeURIComponent(token)}&wallet=${encodeURIComponent(user.address)}&filename=${encodeURIComponent(downloadFilename)}`
      );

      // Fetch as blob so the browser always uses our filename (cross-origin anchor-click
      // ignores the download attribute and falls back to the URL path as the filename)
      const fileResponse = await fetch(proxyUrl);
      if (fileResponse.status === 404) {
        haptic.error();
        notify.error('Download link expired, please try again', user.address);
        return;
      }
      if (!fileResponse.ok) {
        haptic.error();
        notify.error('Download failed — please try again', user.address);
        return;
      }
      const blob = await fileResponse.blob();
      const blobUrl = URL.createObjectURL(blob);
      haptic.success();
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);

      // Fire-and-forget: increment downloadCount on the backend
      try {
        const headers: Record<string, string> = { ...getPoofAPIHeaders(), Authorization: `Bearer ${token}`, 'X-Wallet-Address': user.address };
        const incrementUrl = getPoofAPIUrl(`/api/wallpapers/${wallpaper.id}/download`);
        fetch(incrementUrl, { method: 'POST', headers }).catch(() => {/* non-critical */});
      } catch {
        // Non-critical — don't surface to user
      }
    } catch (err) {
      console.error('Download failed:', err);
      haptic.error();
      notify.error('Download failed — please try again', user?.address);
    }
  }, [user]);

  return { downloadWallpaper };
}
